import { useState, useEffect } from 'react';
import { useAuth } from '../components/Auth/AuthContext';
import userInteractionService from '../services/userInteractionService';

export function useWishlists() {
  const { token, isAuthenticated } = useAuth();
  const [wishlists, setWishlists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [spotWishlistsMap, setSpotWishlistsMap] = useState(new Map()); // Map of spotId -> wishlist IDs

  // Load wishlists on auth state change
  useEffect(() => {
    if (isAuthenticated && token) {
      loadWishlists();
    } else {
      setWishlists([]);
      setSpotWishlistsMap(new Map());
    }
  }, [isAuthenticated, token]);

  const loadWishlists = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const userWishlists = await userInteractionService.getWishlists(token);
      setWishlists(userWishlists);
      
      // Create map of spot -> wishlists for quick lookup
      const spotMap = new Map();
      userWishlists.forEach(wishlist => {
        if (wishlist.spot_ids) {
          wishlist.spot_ids.forEach(spotId => {
            if (!spotMap.has(spotId)) {
              spotMap.set(spotId, []);
            }
            spotMap.get(spotId).push(wishlist.id);
          });
        }
      });
      setSpotWishlistsMap(spotMap);

    } catch (error) {
      console.error('Error loading wishlists:', error);
    } finally {
      setLoading(false);
    }
  };

  const createWishlist = async (wishlistData) => {
    if (!isAuthenticated || !token) {
      throw new Error('User must be authenticated to create wishlists');
    }

    try {
      const newWishlist = await userInteractionService.createWishlist(wishlistData, token);
      setWishlists(prev => [newWishlist, ...prev]);
      return newWishlist;
    } catch (error) {
      console.error('Error creating wishlist:', error);
      throw error;
    }
  };

  const addSpotToWishlist = async (wishlistId, spotId) => {
    if (!isAuthenticated || !token) {
      throw new Error('User must be authenticated to modify wishlists');
    }

    try {
      await userInteractionService.addSpotToWishlist(wishlistId, spotId, token);
      
      // Update local state
      setWishlists(prev => prev.map(wishlist => {
        if (wishlist.id === wishlistId) {
          const updatedSpotIds = wishlist.spot_ids || [];
          if (!updatedSpotIds.includes(spotId)) {
            return {
              ...wishlist,
              spot_ids: [...updatedSpotIds, spotId]
            };
          }
        }
        return wishlist;
      }));

      // Update spot wishlists map
      setSpotWishlistsMap(prev => {
        const newMap = new Map(prev);
        const spotWishlists = newMap.get(spotId) || [];
        if (!spotWishlists.includes(wishlistId)) {
          newMap.set(spotId, [...spotWishlists, wishlistId]);
        }
        return newMap;
      });

    } catch (error) {
      console.error('Error adding spot to wishlist:', error);
      throw error;
    }
  };

  const removeSpotFromWishlist = async (wishlistId, spotId) => {
    if (!isAuthenticated || !token) {
      throw new Error('User must be authenticated to modify wishlists');
    }

    try {
      await userInteractionService.removeSpotFromWishlist(wishlistId, spotId, token);
      
      // Update local state
      setWishlists(prev => prev.map(wishlist => {
        if (wishlist.id === wishlistId) {
          return {
            ...wishlist,
            spot_ids: (wishlist.spot_ids || []).filter(id => id !== spotId)
          };
        }
        return wishlist;
      }));

      // Update spot wishlists map
      setSpotWishlistsMap(prev => {
        const newMap = new Map(prev);
        const spotWishlists = newMap.get(spotId) || [];
        const updatedWishlists = spotWishlists.filter(id => id !== wishlistId);
        
        if (updatedWishlists.length === 0) {
          newMap.delete(spotId);
        } else {
          newMap.set(spotId, updatedWishlists);
        }
        return newMap;
      });

    } catch (error) {
      console.error('Error removing spot from wishlist:', error);
      throw error;
    }
  };

  // Helper functions
  const getSpotWishlists = (spotId) => {
    const wishlistIds = spotWishlistsMap.get(spotId) || [];
    return wishlists.filter(wishlist => wishlistIds.includes(wishlist.id));
  };

  const isSpotInWishlist = (spotId, wishlistId) => {
    const spotWishlists = spotWishlistsMap.get(spotId) || [];
    return spotWishlists.includes(wishlistId);
  };

  const isSpotInAnyWishlist = (spotId) => {
    return spotWishlistsMap.has(spotId) && spotWishlistsMap.get(spotId).length > 0;
  };

  const getSpotWishlistCount = (spotId) => {
    return (spotWishlistsMap.get(spotId) || []).length;
  };

  return {
    wishlists,
    loading,
    spotWishlistsMap,
    getSpotWishlists,
    isSpotInWishlist,
    isSpotInAnyWishlist,
    getSpotWishlistCount,
    createWishlist,
    addSpotToWishlist,
    removeSpotFromWishlist,
    loadWishlists
  };
}

export default useWishlists;